import { Button } from "@abbiamo/ui";
import {
  isRequestedContextDenied,
  PortalLoadingState,
  PortalSelection,
  PortalShell,
  PortalState,
} from "@seller-hermes/features/portal/components/portal-selection";
import { PortalColdEntrySelection } from "@seller-hermes/features/portal/components/portal-cold-entry";
import {
  isPortalColdEntryProduct,
  isValidReturnTo,
} from "@seller-hermes/features/portal/portal-return-to";
import { PortalService } from "@seller-hermes/features/portal/portal.api";
import {
  handlePortalAccessError,
} from "@seller-hermes/features/portal/portal-errors";
import {
  getPortalToastCode,
  PortalToast,
  PortalToastCode,
  removePortalToastErrorFromUrl,
} from "@seller-hermes/features/portal/components/portal-toast";
import {
  getPortalMockScenario,
  portalMockResponses,
} from "@seller-hermes/features/portal/portal.mock";
import { AlertTriangle, Inbox } from "lucide-react";
import { Auth } from "aws-amplify";
import * as React from "react";
import { useQuery } from "react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { publishLogoutMarker } from "../../app/auth-session-expiry";

const PORTAL_STALE_TIME = 5 * 60_000; // 5 minutes
const PORTAL_PAGE_CLASS =
  "flex min-h-screen items-center justify-center bg-ab-bg p-ab-6";

export const PortalPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [toastCode, setToastCode] = React.useState<PortalToastCode | null>(() =>
    getPortalToastCode(searchParams.get("error")),
  );
  const rawScenario = searchParams.get("scenario");
  const scenario = rawScenario ? getPortalMockScenario(rawScenario) : null;

  // Entrada fria por produto (?product=X&return_to=Y) — usuário sem sessão
  // chegou direto num produto satélite (Roteirizamo/Insights) e foi mandado
  // pro Portal. product/return_to inválidos ou ausentes caem no fluxo normal
  // sem filtro nenhum — nunca travam a aplicação.
  const rawProduct = searchParams.get("product");
  const rawReturnTo = searchParams.get("return_to");
  const coldEntry =
    isPortalColdEntryProduct(rawProduct) && rawReturnTo && isValidReturnTo(rawReturnTo)
      ? { product: rawProduct, returnTo: rawReturnTo }
      : null;

  React.useEffect(() => {
    if (!toastCode) return;
    const url = removePortalToastErrorFromUrl(window.location.href);
    window.history.replaceState(window.history.state, "", url);
  }, [toastCode]);

  const portalMeQuery = useQuery(
    ["portal-me-for-portal"],
    () => PortalService.getMeForPortal(),
    {
      enabled: !scenario,
      staleTime: PORTAL_STALE_TIME,
      retry: false,
      onError: (error: unknown) => {
        void handlePortalAccessError(error, {
          signOut: () => {
            publishLogoutMarker();
            return Auth.signOut().catch(() => undefined);
          },
          navigate,
        });
      },
    },
  );

  // Handoff do dominio legado pediu um ambiente ao qual esta sessao nao tem
  // acesso. getPortalDecision ja segurou o usuario aqui; o toast explica por
  // que ele caiu no seletor em vez de ir direto para onde estava.
  React.useEffect(() => {
    if (!portalMeQuery.data) return;
    if (!isRequestedContextDenied(portalMeQuery.data)) return;

    setToastCode("no-access-to-requested-context");
  }, [portalMeQuery.data]);

  if (scenario === "loading") {
    return <PortalLoadingState />;
  }

  if (scenario === "error") {
    return (
      <main className={PORTAL_PAGE_CLASS}>
        <PortalShell>
          <PortalState
            icon={<AlertTriangle size={28} aria-hidden />}
            title="Nao foi possivel carregar suas operacoes"
            description="Este estado simula uma falha de contexto. Nenhuma chamada real de API foi feita."
            action={
              <Button variant="outline" onClick={() => navigate("/")}>
                Tentar novamente
              </Button>
            }
          />
        </PortalShell>
      </main>
    );
  }

  if (!scenario && portalMeQuery.isError) {
    return (
      <main className={PORTAL_PAGE_CLASS}>
        {toastCode ? (
          <PortalToast code={toastCode} onClose={() => setToastCode(null)} />
        ) : null}
        <PortalShell>
          <PortalState
            icon={<AlertTriangle size={28} aria-hidden />}
            title="Nao foi possivel carregar suas operacoes"
            description="Tente novamente. Se o problema persistir, entre em contato com o suporte."
            action={
              <Button
                variant="outline"
                onClick={() => portalMeQuery.refetch()}
              >
                Tentar novamente
              </Button>
            }
          />
        </PortalShell>
      </main>
    );
  }

  if (!scenario && (portalMeQuery.isLoading || !portalMeQuery.data)) {
    return <PortalLoadingState />;
  }

  const portalMe = scenario
    ? portalMockResponses[scenario]
    : portalMeQuery.data!;

  if (coldEntry) {
    return (
      <main className={PORTAL_PAGE_CLASS}>
        <PortalShell>
          <PortalColdEntrySelection
            portalMe={portalMe}
            product={coldEntry.product}
            returnTo={coldEntry.returnTo}
          />
        </PortalShell>
      </main>
    );
  }

  const PROFILE_ORDER: Record<string, number> = { LOG: 0, GO: 1 };
  const visibleSellerGroups = portalMe.seller_groups
    .filter((context) => context.profile !== "PAY")
    .sort(
      (a, b) =>
        (PROFILE_ORDER[a.profile] ?? 99) - (PROFILE_ORDER[b.profile] ?? 99),
    );

  if (visibleSellerGroups.length === 0) {
    return (
      <main className={PORTAL_PAGE_CLASS}>
        <PortalShell>
          <PortalState
            icon={<Inbox size={28} aria-hidden />}
            title="Nenhuma operacao vinculada"
            description="Este usuario esta autenticado, mas ainda nao possui operacoes LOG ou GO disponiveis."
            action={
              <Button variant="outline" onClick={() => navigate("/login")}>
                Voltar ao login
              </Button>
            }
          />
        </PortalShell>
      </main>
    );
  }

  return (
    <main className={PORTAL_PAGE_CLASS}>
      {toastCode ? (
        <PortalToast code={toastCode} onClose={() => setToastCode(null)} />
      ) : null}
      <PortalShell>
        <PortalSelection
          portalMe={{ ...portalMe, seller_groups: visibleSellerGroups }}
        />
      </PortalShell>
    </main>
  );
};
